import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Link, NavLink, useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { AuthProvider, useAuth, API } from "@/AuthContext";
import { I18nProvider, useI18n } from "@/i18n";
import Home from "@/pages/Home";
import Recipes from "@/pages/Recipes";
import Bundles from "@/pages/Bundles";
import GiftClaim from "@/pages/GiftClaim";
import RecipeDetail from "@/pages/RecipeDetail";
import Dashboard from "@/pages/Dashboard";
import Admin from "@/pages/Admin";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Cookies from "@/pages/Cookies";
import { SocialLinks } from "@/components/SocialLinks";
import CookieBanner from "@/components/CookieBanner";
import "@/App.css";

function Header() {
  const { user, logout } = useAuth();
  const { lang, setLang, supported, t } = useI18n();
  const nav = useNavigate();
  return (
    <header className="sticky top-0 z-40 glass" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between gap-4">
        <Link to="/" className="font-serif text-lg lg:text-xl tracking-tight whitespace-nowrap" data-testid="brand-link">
          <span className="gold">JS</span> · {t("brand")}
        </Link>
        <nav className="hidden md:flex items-center gap-7 text-[11px] uppercase tracking-[0.18em] text-zinc-300">
          <NavLink to="/recipes" className={({isActive}) => isActive ? "gold" : ""} data-testid="nav-recipes">{t("nav_portfolio")}</NavLink>
          <NavLink to="/bundles" className={({isActive}) => isActive ? "gold" : ""} data-testid="nav-bundles">{t("nav_bundles")}</NavLink>
          <a href="/#capitals" className="hover:text-white" data-testid="nav-capitals">{t("nav_capitals")}</a>
          <a href="/#atelier" className="hover:text-white" data-testid="nav-atelier">{t("nav_about")}</a>
          {user && <NavLink to="/dashboard" className={({isActive}) => isActive ? "gold" : ""} data-testid="nav-dashboard">{t("nav_dashboard")}</NavLink>}
          {user?.is_admin && <NavLink to="/admin" className={({isActive}) => isActive ? "gold" : ""} data-testid="nav-admin">{t("nav_admin")}</NavLink>}
        </nav>
        <div className="flex items-center gap-3">
          <div className="flex items-center text-[10px] tracking-[0.18em]" data-testid="lang-toggle">
            {supported.map((l, i) => (
              <React.Fragment key={l}>
                {i > 0 && <span className="text-zinc-700 px-0.5">/</span>}
                <button onClick={() => setLang(l)} className={`px-1.5 py-1 ${lang===l?"gold":"text-zinc-500 hover:text-zinc-300"}`} data-testid={`lang-${l}`}>{l.toUpperCase()}</button>
              </React.Fragment>
            ))}
          </div>
          {user ? (
            <button onClick={async () => { await logout(); nav("/"); }} className="btn-ghost text-[11px] !py-2 !px-4" data-testid="logout-btn">{t("nav_logout")}</button>
          ) : (
            <Link to="/login" className="btn-gold text-[11px] !py-2 !px-4" data-testid="login-btn">{t("nav_login")}</Link>
          )}
        </div>
      </div>
    </header>
  );
}

function Footer() {
  const { t } = useI18n();
  return (
    <footer className="border-t border-white/5 mt-32" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 flex flex-col md:flex-row gap-8 justify-between items-start">
        <div>
          <div className="font-serif text-2xl tracking-tight"><span className="gold">JS</span> · {t("brand")}</div>
          <p className="text-zinc-500 text-sm mt-2 max-w-md">{t("footer_priv")} — jacintof.samuel@gmail.com</p>
        </div>
        <SocialLinks label={t("footer_follow")} />
        <div className="flex flex-col items-start md:items-end gap-3">
          <nav className="flex gap-4 text-xs text-zinc-400" data-testid="legal-links">
            <Link to="/terms" className="hover:text-[#D4AF37] transition-colors">{t("footer_terms")}</Link>
            <Link to="/privacy" className="hover:text-[#D4AF37] transition-colors">{t("footer_privacy")}</Link>
            <Link to="/cookies" className="hover:text-[#D4AF37] transition-colors">{t("footer_cookies")}</Link>
          </nav>
          <div className="overline">© {new Date().getFullYear()} · {t("brand")}</div>
        </div>
      </div>
    </footer>
  );
}

function AppRouter() {
  const location = useLocation();
  // Synchronous detection prevents race conditions
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <Header />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/recipes" element={<Recipes />} />
        <Route path="/bundles" element={<Bundles />} />
        <Route path="/gifts/claim/:token" element={<GiftClaim />} />
        <Route path="/recipes/:id" element={<RecipeDetail />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/login" element={<Login />} />
        <Route path="/checkout/success" element={<CheckoutSuccess />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/cookies" element={<Cookies />} />
      </Routes>
      <Footer />
      <CookieBanner />
    </>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster theme="dark" position="top-center" />
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}
